import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Input, Modal, Empty } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { AgentConversationSummary } from '../../types/agent';
import styles from './ConversationHistoryList.module.css';

interface ConversationHistoryListProps {
  conversations: AgentConversationSummary[];
  activeId?: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onRename?: (id: number, title: string) => void;
  onDelete?: (id: number) => void;
}

type Bucket = 'Сьогодні' | 'Вчора' | 'Останні 7 днів' | 'Раніше';

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function bucketOf(updatedAt?: string | null): Bucket {
  if (!updatedAt) return 'Раніше';
  const ts = new Date(updatedAt).getTime();
  if (Number.isNaN(ts)) return 'Раніше';
  const today = startOfDay(new Date());
  const day = 86_400_000;
  if (ts >= today) return 'Сьогодні';
  if (ts >= today - day) return 'Вчора';
  if (ts >= today - 7 * day) return 'Останні 7 днів';
  return 'Раніше';
}

const BUCKET_ORDER: Bucket[] = ['Сьогодні', 'Вчора', 'Останні 7 днів', 'Раніше'];

const ConversationHistoryList: React.FC<ConversationHistoryListProps> = ({
  conversations = [],
  activeId,
  onSelect,
  onNew,
  onRename,
  onDelete,
}) => {
  const [query, setQuery] = useState('');
  const [renaming, setRenaming] = useState<AgentConversationSummary | null>(null);
  const [renameText, setRenameText] = useState('');

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = conversations ?? [];
    const filtered = q
      ? all.filter((c) => (c.title || '').toLowerCase().includes(q))
      : all;
    const map = new Map<Bucket, AgentConversationSummary[]>();
    for (const c of filtered) {
      const b = bucketOf(c.updated_at);
      (map.get(b) ?? map.set(b, []).get(b)!).push(c);
    }
    return BUCKET_ORDER.filter((b) => map.has(b)).map((b) => ({
      bucket: b,
      items: map.get(b)!,
    }));
  }, [conversations, query]);

  const openRename = (conv: AgentConversationSummary) => {
    setRenaming(conv);
    setRenameText(conv.title || '');
  };

  const submitRename = () => {
    const title = renameText.trim();
    if (renaming && title && onRename) onRename(renaming.id, title);
    setRenaming(null);
  };

  const itemMenu = (conv: AgentConversationSummary): MenuProps['items'] => [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: 'Перейменувати',
      onClick: () => openRename(conv),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Видалити',
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: 'Видалити розмову?',
          content: conv.title || 'Без назви',
          okText: 'Видалити',
          okButtonProps: { danger: true },
          cancelText: 'Скасувати',
          onOk: () => onDelete?.(conv.id),
        }),
    },
  ];

  return (
    <div className={styles.root}>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        block
        onClick={onNew}
        className={styles.newBtn}
      >
        Новий чат
      </Button>

      <Input.Search
        placeholder="Пошук розмов…"
        allowClear
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={styles.search}
      />

      {grouped.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={query ? 'Нічого не знайдено' : 'Поки що немає розмов'}
        />
      ) : (
        <div className={styles.scroll}>
          {grouped.map(({ bucket, items }) => (
            <div key={bucket} className={styles.group}>
              <div className={styles.groupLabel}>{bucket}</div>
              {items.map((conv) => (
                <div
                  key={conv.id}
                  className={`${styles.item} ${
                    conv.id === activeId ? styles.itemActive : ''
                  }`}
                  onClick={() => onSelect(conv.id)}
                >
                  <span className={styles.itemTitle}>
                    {conv.title || 'Без назви'}
                  </span>
                  <Dropdown
                    menu={{ items: itemMenu(conv) }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      className={styles.itemMenuBtn}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Modal
        title="Перейменувати розмову"
        open={renaming !== null}
        onOk={submitRename}
        onCancel={() => setRenaming(null)}
        okText="Зберегти"
        cancelText="Скасувати"
        okButtonProps={{ disabled: !renameText.trim() }}
        destroyOnClose
      >
        <Input
          value={renameText}
          onChange={(e) => setRenameText(e.target.value)}
          onPressEnter={submitRename}
          maxLength={255}
          autoFocus
          placeholder="Нова назва"
        />
      </Modal>
    </div>
  );
};

export default ConversationHistoryList;
